import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./ccSpotlight.css";

import davidsonCampus from "../../../assets/davidson-college.webp";
import wabashCampus from "../../../assets/wabash-campus.webp";
import hamiltonCampus from "../../../assets/hamilton-college.webp";
import davidsonLogo from "../../../assets/davidson-logo.webp";
import wabashLogo from "../../../assets/wabash-logo.webp";
/* The trimmed copy, like the logo rows use — the untrimmed file carries
   enough padding that it renders visibly smaller than the other two marks. */
import hamiltonLogo from "../../../assets/Schools/trimmed/Hamilton College.webp";

/* One story at a time inside a single split card: the campus fills the left
   pane, the quote sits on grey beside it with the school's mark above it.
   Arrows sit above the card and the schools sit under it, taking the place of
   the reference's dots so you can see what you are switching to.

   `crop` is per school because the shots do not frame alike. Wabash is an
   aerial with a sunset sky across its top fifth, so it is oversized and hung
   above the frame to cut that band; the other two are full-bleed campus with
   nothing to lose at the top, and hanging them the same way would take the
   chapel steeple off Davidson. Anything without a crop gets plain cover. */
const stories = [
  {
    school: "Wabash College",
    campus: wabashCampus,
    logo: wabashLogo,
    crop: { "--crop-top": "-36%", "--crop-h": "136%" },
    quote:
      "I have been in Career Services around 10 years and I can't think of any other tech, service, app, or platform we've ever launched that we have gotten this kind of engagement from students this quickly.",
    name: "Cassie Hagan",
    role: "Director of Professional Development & CIBE",
  },
  {
    school: "Davidson College",
    campus: davidsonCampus,
    logo: davidsonLogo,
    quote:
      "RAIN has exceeded all expectations! It gives students a centralized way to track applications, follow a personalized task schedule, and stay on top of deadlines, making it an outstanding tool for today's individualized internship and job search.",
    name: "Liz Wilson",
    role: "Assistant Director for Career Development, Business & Finance",
  },
  {
    school: "Hamilton College",
    campus: hamiltonCampus,
    logo: hamiltonLogo,
    /* A stacked mark among two wide wordmarks: at the shared 40px cap it
       carries visibly less ink than they do, so it gets the same 1.67 the
       logo row further up the page gives it. */
    logoStyle: { "--logo-h": "67px" },
    quote:
      "The RAIN team is responsive, collaborative, and genuinely open to feedback. RAIN has streamlined our work behind the scenes, making it easier to support students at scale.",
    name: "Victoria Vargulick",
    role: "Director of Career Development",
  },
];

const CcSpotlight = () => {
  const [active, setActive] = useState(0);
  const story = stories[active];
  const step = (d) =>
    setActive((i) => (i + d + stories.length) % stories.length);

  return (
    <section className="cc-section cc-spotlight" id="spotlight">
      <div className="home-container">
        <div className="cc-spot-nav">
          <button
            type="button"
            className="cc-spot-arrow"
            onClick={() => step(-1)}
            aria-label="Previous story"
          >
            <ArrowLeft size={18} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="cc-spot-arrow cc-spot-arrow-on"
            onClick={() => step(1)}
            aria-label="Next story"
          >
            <ArrowRight size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="cc-spot-card">
          <figure className="cc-spot-campus">
            <AnimatePresence initial={false}>
              <motion.img
                key={story.school}
                src={story.campus}
                loading="lazy"
                alt={`${story.school} campus`}
                style={story.crop}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 0.6, ease: "linear" },
                  scale: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                }}
              />
            </AnimatePresence>
          </figure>

          <motion.div
            key={story.school}
            className="cc-spot-panel"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              decoding="async"
              loading="lazy"
              className="cc-spot-logo"
              src={story.logo}
              alt={story.school}
              style={story.logoStyle}
            />

            <blockquote className="cc-spot-quote">{story.quote}</blockquote>

            <p className="cc-spot-name">{story.name}</p>
            {/* The mark above the quote already names the school. */}
            <p className="cc-spot-role">{story.role}</p>

            <a className="cc-spot-btn" href="#book">
              Book a walkthrough
            </a>
          </motion.div>
        </div>

        {/* The schools stand in for the reference's dots. */}
        <div className="cc-spot-switch">
          {stories.map((item, i) => (
            <button
              type="button"
              key={item.school}
              className={`cc-switch${i === active ? " cc-switch-on" : ""}`}
              onClick={() => setActive(i)}
              aria-pressed={i === active}
            >
              <img decoding="async" loading="lazy" src={item.campus} alt="" />
              <span>{item.school}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CcSpotlight;
